import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, CheckCircle2, AlertTriangle, ExternalLink, Unlink, Link as LinkIcon, Save } from 'lucide-react';
import apiClient from '../utils/apiClient';
import { isValidEnvVarName } from '../utils/envVarName';
import { shouldShowDisconnectButton } from '../utils/stripeConnectVisibility';
import { useFacility } from '../context/FacilityContext';

const StripeSecretEnvVarField = ({ facilityId }) => {
  const [value, setValue] = useState('');
  const [initial, setInitial] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/api/facilities/${facilityId}`);
      const v = r.data?.stripe_secret_env_var || '';
      setValue(v);
      setInitial(v);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    const trimmed = (value || '').trim();
    if (trimmed && !isValidEnvVarName(trimmed)) {
      setError('Env var name must be UPPERCASE letters, digits, and underscores only.');
      return;
    }
    setSaving(true); setError(null); setInfo(null);
    try {
      await apiClient.put(`/api/facilities/${facilityId}`, {
        // Normalize blank -> null so the backend clears the field rather
        // than trying to validate an empty string against the regex.
        stripe_secret_env_var: trimmed ? trimmed : null,
      });
      setInitial(trimmed);
      setInfo('Saved.');
      setTimeout(() => setInfo(null), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const dirty = (value || '').trim() !== (initial || '').trim();

  return (
    <div className="rounded-xl border border-slate-200/80 bg-white p-6 mt-4">
      <div className="mb-3">
        <h3 className="text-[14px] font-semibold text-slate-900">Per-facility Stripe key (advanced)</h3>
        <p className="text-[12px] text-slate-500 mt-1">
          Names the Railway environment variable that holds this facility's Stripe secret key.
          Leave blank to use the platform default (<code>STRIPE_SECRET_KEY</code>). Used when one
          portfolio runs multiple LLCs that each own their own Stripe account.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-center gap-2 mb-3">
          <AlertTriangle className="h-3.5 w-3.5" /> {error}
        </div>
      )}
      {info && (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-700 flex items-center gap-2 mb-3">
          <CheckCircle2 className="h-3.5 w-3.5" /> {info}
        </div>
      )}

      <input
        type="text"
        placeholder="e.g. STRIPE_FACILITYSLUG_SECRET_KEY"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={loading || saving}
        style={{fontFamily: "monospace"}}
        className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-violet-500"
      />
      <p className="text-[11px] text-slate-400 mt-1">
        Uppercase letters, digits, and underscores only. The secret value is stored on Railway, not in mySMS360.
        Contact mySMS360 platform support to provision the env var before pointing this field at it.
      </p>

      <div className="pt-3 flex justify-end">
        <button
          onClick={onSave}
          disabled={saving || loading || !dirty}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-md text-[13px] font-medium"
        >
          <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

const StripeConnect = () => {
  const { currentFacility } = useFacility();
  const facilityId = currentFacility?._id || currentFacility?.id;
  const [params, setParams] = useSearchParams();
  const justOnboarded = params.get('onboarded') === '1';
  const oauthCode = params.get('code');
  const oauthState = params.get('state');

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [oauthMessage, setOauthMessage] = useState(null);

  // Direct-attach (Option C) — for Path 1 operators who activated their
  // Stripe account on dashboard.stripe.com and just need the acct_id
  // written to the facility doc. See memory:
  // project_stripe_accounts_per_llc.
  const [acctIdToAttach, setAcctIdToAttach] = useState('');
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState(null);
  const [attachSuccess, setAttachSuccess] = useState(null);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.get(`/api/facilities/${facilityId}/stripe/status`);
      setStatus(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  // Detect Stripe OAuth redirect (Option B — link existing account flow).
  // After the operator approves on Stripe, we land back here with ?code=...
  // POST it to /oauth-callback to exchange for the account id.
  useEffect(() => {
    if (!facilityId || !oauthCode) return;
    // Verify the state matches this facility — guards against XSRF / wrong tab.
    if (oauthState && !oauthState.startsWith(`${facilityId}|`)) {
      setError(`OAuth state mismatch (got "${oauthState}") — likely a stale redirect. Retry from Settings.`);
      setParams({}, { replace: true });
      return;
    }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await apiClient.post(
          `/api/facilities/${facilityId}/stripe/oauth-callback`,
          { code: oauthCode }
        );
        setOauthMessage(`Linked Stripe account ${r.data.account_id}.`);
        setParams({}, { replace: true });
        await load();
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, oauthCode]);

  const startOnboarding = async () => {
    setLoading(true);
    setError(null);
    try {
      const returnUrl = `${window.location.origin}/settings/stripe?onboarded=1`;
      const r = await apiClient.post(
        `/api/facilities/${facilityId}/stripe/onboarding-link`,
        { return_url: returnUrl, refresh_url: returnUrl }
      );
      window.location.href = r.data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setLoading(false);
    }
  };

  const startOAuthLink = async () => {
    setLoading(true);
    setError(null);
    try {
      const returnUrl = `${window.location.origin}/settings/stripe`;
      const r = await apiClient.get(
        `/api/facilities/${facilityId}/stripe/oauth-link?return_url=${encodeURIComponent(returnUrl)}`
      );
      window.location.href = r.data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setLoading(false);
    }
  };

  const attachAccountId = async () => {
    setAttaching(true);
    setAttachError(null);
    setAttachSuccess(null);
    try {
      const res = await apiClient.post(
        `/api/facilities/${facilityId}/stripe/attach-existing-account`,
        { stripe_account_id: acctIdToAttach.trim() }
      );
      setAttachSuccess(
        `Attached ${res.data.account_id}. ` +
        `charges_enabled=${res.data.charges_enabled}, ` +
        `payouts_enabled=${res.data.payouts_enabled}. ` +
        `Refresh the page to see updated status.`
      );
      await load();
    } catch (e) {
      setAttachError(e?.response?.data?.detail || e.message);
    } finally {
      setAttaching(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(
      'Disconnect this Stripe account from this facility? This doesn\'t delete the Stripe account itself, just removes the link in mySMS360. Are you sure?'
    )) return;
    setLoading(true);
    try {
      await apiClient.post(`/api/facilities/${facilityId}/stripe/disconnect`);
      setOauthMessage('Stripe account disconnected from this facility.');
      setAttachSuccess(null);
      setAttachError(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const onboarded = status?.onboarded;
  const connected = status?.connected;
  const requirementsDue = status?.requirements_due || [];
  // Direct-mode signal: Stripe says charges + details are live, so this
  // account was activated outside the platform's Connect flow. Suppress
  // the "Continue onboarding" CTA — AccountLink.create would 503 on a
  // non-Connect-managed account.
  const isExternallyOnboarded = status?.is_externally_onboarded;

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-violet-600" />
          Stripe Connect
        </h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          Tenants pay rent online via your own Stripe account.
        </p>
      </div>

      {justOnboarded && (
        <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/50 p-4 mb-5">
          <p className="text-[13px] text-emerald-700">
            <span className="font-medium">Returned from Stripe.</span> Refreshing connection status...
          </p>
        </div>
      )}

      {oauthMessage && (
        <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/50 p-4 mb-5">
          <p className="text-[13px] text-emerald-700 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            <span className="font-medium">Linked.</span> {oauthMessage}
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-rose-200/80 bg-rose-50/50 p-4 mb-5">
          <p className="text-[13px] text-rose-700">{error}</p>
        </div>
      )}

      {/* Stripe API itself errored on the status fetch (per-facility key
          wrong, platform key missing, account_id stale, etc). Surface the
          exact Stripe message + the most common causes so the operator can
          self-diagnose without filing a support ticket. Only render this
          when an account_id is on file — if the facility hasn't attached
          a Stripe account yet, the "Not connected" card + Attach UI below
          is the right surface, not a red error banner. */}
      {status?.error && status?.account_id && (
        <div className="rounded-xl border border-red-200 bg-red-50/40 p-5 mb-5">
          <p className="text-red-800 font-semibold text-sm">
            Couldn't reach Stripe for this facility
          </p>
          <p className="text-xs text-slate-600 mt-1">
            Stripe API returned: <code>{status.error}</code>
          </p>
          <p className="text-xs text-slate-600 mt-2">
            Common causes: (1) per-facility env var <code>{currentFacility?.stripe_secret_env_var || '(none set)'}</code> doesn't match the Stripe account, OR (2) platform default <code>STRIPE_SECRET_KEY</code> missing.
          </p>
        </div>
      )}

      {/* Clear/Disconnect — always visible when an account_id is attached,
          regardless of error / onboarded state. Operators need this to
          reset a stale stripe_account_id (e.g., switching from a broken
          LLC account to a working one) without being forced through any
          particular UI branch. MANAGE_MERCHANT_CONFIG is enforced
          server-side; nothing client-side gates this button beyond the
          presence of an account id to clear. */}
      {shouldShowDisconnectButton(status) && (
        <div className="rounded-xl border border-rose-200/80 bg-rose-50/30 p-4 mb-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-[13px] font-semibold text-slate-900">
              Clear Stripe account from this facility
            </p>
            <p className="text-[12px] text-slate-600 mt-0.5">
              Removes the link to <code className="font-mono text-[11px]">{status.account_id}</code> in mySMS360. The Stripe account itself is untouched and can be re-attached at any time. Use this when the stored account ID is stale or pointing at the wrong LLC.
            </p>
          </div>
          <button
            onClick={disconnect}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-lg text-[13px] font-medium shrink-0"
          >
            <Unlink className="h-3.5 w-3.5" />
            Clear Stripe account
          </button>
        </div>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white p-6">
        {loading && !status ? (
          <p className="text-[13px] text-slate-400">Loading...</p>
        ) : !connected ? (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-[15px] font-semibold text-slate-900">Not connected</h2>
                <p className="text-[13px] text-slate-500">Connect your Stripe account so tenants can pay rent online.</p>
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 p-4 text-[13px] text-slate-600 mb-5 space-y-2">
              <p><span className="font-medium text-slate-900">You own the account.</span> Stripe Standard means your account is in your name, with your bank. If you ever leave mySMS360, you keep your account, your card vault, your customer history, and your dispute record.</p>
              <p><span className="font-medium text-slate-900">No platform fees.</span> mySMS360 charges no markup on transactions. Stripe's standard rate (2.9% + $0.30 cards, 0.8% capped $5 ACH) goes directly to Stripe.</p>
              <p><span className="font-medium text-slate-900">Payouts are yours.</span> Stripe deposits to your bank on Stripe's schedule — mySMS360 never holds your money.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={startOnboarding}
                disabled={loading || !facilityId}
                className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg text-[13px] font-medium"
              >
                <CreditCard className="h-4 w-4" />
                Create new Stripe account
              </button>
              <button
                onClick={startOAuthLink}
                disabled={loading || !facilityId}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 disabled:opacity-50 text-violet-700 border border-violet-200 rounded-lg text-[13px] font-medium"
              >
                <LinkIcon className="h-4 w-4" />
                Link an existing Stripe account
              </button>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              "Link" if you already created a Stripe account for this LLC outside mySMS360. "Create" if this is your first time setting up Stripe for this entity.
            </p>

            {/* Option C — direct attach (bypass OAuth + AccountLink). Only
                relevant when no STRIPE_CONNECT_CLIENT_ID is registered on
                the platform (Path 1 / direct-mode) and the operator already
                activated the Stripe account on dashboard.stripe.com.
                Backend MANAGE_MERCHANT_CONFIG enforces auth. */}
            <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50/40 p-5">
              <h3 className="font-semibold text-slate-900 text-sm">
                Attach existing Stripe account (advanced)
              </h3>
              <p className="text-xs text-slate-600 mt-1 mb-3">
                Use this if you activated your Stripe account directly on
                dashboard.stripe.com (not via mySMS360's onboarding). Paste the
                account ID (starts with <code>acct_</code>).
              </p>
              <input
                type="text"
                value={acctIdToAttach}
                onChange={(e) => setAcctIdToAttach(e.target.value)}
                placeholder="acct_..."
                className="w-full px-3 py-2 rounded border border-slate-300 font-mono text-sm"
              />
              <button
                onClick={attachAccountId}
                disabled={!acctIdToAttach.startsWith('acct_') || attaching}
                className="mt-3 px-4 py-2 rounded bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
              >
                {attaching ? 'Verifying with Stripe...' : 'Attach account'}
              </button>
              {attachError && (
                <p className="mt-2 text-xs text-red-700">{attachError}</p>
              )}
              {attachSuccess && (
                <p className="mt-2 text-xs text-emerald-700">{attachSuccess}</p>
              )}
            </div>
          </div>
        ) : !onboarded ? (
          <div>
            {/* Two sub-cases when account is connected but not onboarded:
                (a) details_submitted=false → account came in via the
                    platform's Connect/AccountLink flow but operator never
                    finished. Show "Continue onboarding" — AccountLink.create
                    will work because the account is Connect-managed.
                (b) details_submitted=true but charges_enabled=false →
                    externally onboarded (Path 1: operator activated on
                    dashboard.stripe.com directly), but Stripe is holding
                    charges pending more info. AccountLink.create would 503
                    here — direct them to the Stripe Dashboard instead. */}
            {!status?.details_submitted ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-[15px] font-semibold text-slate-900">Onboarding incomplete</h2>
                    <p className="text-[13px] text-slate-500">Stripe needs a few more details to enable charges.</p>
                  </div>
                </div>
                {requirementsDue.length > 0 && (
                  <div className="rounded-lg bg-amber-50/50 border border-amber-200/80 p-4 mb-5">
                    <p className="text-[12px] font-medium text-amber-700 mb-2">Stripe still needs:</p>
                    <ul className="text-[12px] text-amber-700 list-disc pl-5 space-y-1">
                      {requirementsDue.map((r) => <li key={r}>{r.replace(/[._]/g, ' ')}</li>)}
                    </ul>
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={startOnboarding}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg text-[13px] font-medium"
                  >
                    Continue onboarding
                    <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={load}
                    disabled={loading}
                    className="px-4 py-2 border border-slate-200 hover:border-slate-300 rounded-lg text-[13px] text-slate-700"
                  >
                    Refresh
                  </button>
                </div>
              </>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-5">
                <p className="text-amber-800 font-semibold text-sm">
                  Stripe needs additional info to enable charges
                </p>
                {requirementsDue.length > 0 ? (
                  <ul className="text-xs text-slate-600 mt-2 list-disc list-inside space-y-1">
                    {requirementsDue.map((req, i) => (
                      <li key={i}><code>{req}</code></li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-slate-600 mt-2">
                    Stripe hasn't flagged specific requirements yet — check your dashboard for the latest status.
                  </p>
                )}
                <p className="text-xs text-slate-600 mt-3">
                  Complete these in your Stripe Dashboard (
                  <a
                    href={status?.account_id ? `https://dashboard.stripe.com/${status.account_id}` : 'https://dashboard.stripe.com/'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Open dashboard
                  </a>
                  ).
                </p>
                <button
                  onClick={load}
                  disabled={loading}
                  className="mt-3 px-4 py-2 border border-slate-200 hover:border-slate-300 rounded-lg text-[13px] text-slate-700"
                >
                  Refresh
                </button>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-[15px] font-semibold text-slate-900">Connected</h2>
                <p className="text-[13px] text-slate-500">Stripe is live for this facility.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-[13px] mb-5">
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-0.5">Charges</div>
                <div className={status?.charges_enabled ? 'text-emerald-700' : 'text-amber-700'}>
                  {status?.charges_enabled ? 'Enabled' : 'Pending'}
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-0.5">Payouts</div>
                <div className={status?.payouts_enabled ? 'text-emerald-700' : 'text-amber-700'}>
                  {status?.payouts_enabled ? 'Enabled' : 'Pending'}
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 p-3 col-span-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-0.5">Stripe account</div>
                <div className="font-mono text-[12px] text-slate-700 truncate">{status?.account_id}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <a
                href="https://dashboard.stripe.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 hover:border-slate-300 rounded-lg text-[13px] text-slate-700"
              >
                Open Stripe dashboard
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <button
                onClick={disconnect}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 border border-rose-200 hover:bg-rose-50 text-rose-600 rounded-lg text-[13px]"
              >
                <Unlink className="h-3.5 w-3.5" />
                Disconnect
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Advanced: per-facility Stripe secret key (Pattern A). Lets one
          portfolio that operates multiple LLCs (each its own Stripe
          account) route this facility's charges at the right LLC's bank.
          Owner-only via MANAGE_MERCHANT_CONFIG enforced server-side. */}
      {facilityId && <StripeSecretEnvVarField facilityId={facilityId} />}
    </div>
  );
};

export default StripeConnect;
